import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SpaceMonkeyLogo from '../../components/brand/SpaceMonkeyLogo';
import Icon from '../../components/brand/Icon';
import './EnforcementScanPage.css';

// TODO: Integrate OCR library to extract license plate text from image.
// Options to consider:
//   - Tesseract.js (browser-based OCR): https://github.com/naptha/tesseract.js
//   - OpenALPR cloud API: https://www.openalpr.com
//   - Plate Recognizer API: https://platerecognizer.com
//
// Implementation steps when ready:
//   1. Send image file to OCR service
//   2. Parse returned text for license plate pattern (e.g. /[A-Z0-9]{4,8}/)
//   3. Auto-fill the manual input and optionally auto-submit lookup
const extractPlateFromImage = async (imageFile) => {
  // TODO: Replace this stub with real OCR call
  console.warn('OCR not connected; plate must be entered manually.');
  return null;
};

const EnforcementScanPage = () => {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [manualPlate, setManualPlate] = useState('');
  const [ocrStatus, setOcrStatus] = useState('idle'); // idle | processing | done | error
  const [ocrResult, setOcrResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!localStorage.getItem('authToken')) {
      navigate('/admin/signin');
    }
  }, [navigate]);

  const handleImageCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    setImagePreview(url);
    setOcrStatus('processing');
    setOcrResult(null);

    try {
      const plate = await extractPlateFromImage(file);
      if (plate) {
        setManualPlate(plate.toUpperCase());
        setOcrResult(plate);
        setOcrStatus('done');
      } else {
        setOcrStatus('pending');
      }
    } catch {
      setOcrStatus('error');
    }
  };

  const handleLookup = () => {
    const plate = manualPlate.trim().toUpperCase();
    if (!plate) return;
    navigate(`/enforcement?plate=${encodeURIComponent(plate)}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleLookup();
  };

  return (
    <div className="scan-page">
      <header className="scan-header">
        <div className="scan-header-inner">
          <Link to="/enforcement" className="scan-back">Plate Lookup</Link>
          <SpaceMonkeyLogo size="sm" tagline={false} />
        </div>
      </header>

      <div className="scan-content">
        <div className="scan-box">
          <h1>Scan License Plate</h1>
          <p>Take a plate photo or enter the plate manually.</p>

          {/* Camera Input */}
          <div className="scan-camera-area" onClick={() => fileInputRef.current?.click()}>
            {imagePreview ? (
              <img src={imagePreview} alt="Captured plate" className="scan-preview" />
            ) : (
              <div className="scan-camera-placeholder">
                <span className="scan-camera-icon"><Icon name="camera" size={40} /></span>
                <span className="scan-camera-label">Take plate photo</span>
                <span className="scan-camera-sub">or enter plate manually</span>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageCapture}
              className="scan-file-input"
            />
          </div>

          {imagePreview && (
            <button className="scan-retake" onClick={() => { setImagePreview(null); setOcrStatus('idle'); setOcrResult(null); }}>
              Retake Photo
            </button>
          )}

          {/* Status */}
          {ocrStatus === 'processing' && (
            <div className="scan-ocr-status processing">
              <span className="scan-spinner"></span>
              Reading plate...
            </div>
          )}

          {ocrStatus === 'pending' && (
            <div className="scan-ocr-status pending">
              Enter the plate manually below to continue.
            </div>
          )}

          {ocrStatus === 'done' && ocrResult && (
            <div className="scan-ocr-status done">
              <Icon name="check" size={16} /> Plate detected: <strong>{ocrResult}</strong>
            </div>
          )}

          {ocrStatus === 'error' && (
            <div className="scan-ocr-status error">
              Could not read the plate. Enter it manually below.
            </div>
          )}

          {/* Manual Input */}
          <div className="scan-manual">
            <label className="scan-label">License Plate</label>
            <div className="scan-input-row">
              <input
                className="scan-input"
                placeholder="Type plate here..."
                value={manualPlate}
                onChange={e => setManualPlate(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                maxLength={10}
                autoCapitalize="characters"
              />
              <button
                className="scan-lookup-btn"
                onClick={handleLookup}
                disabled={!manualPlate.trim()}
              >
                Check Plate
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="scan-footer">
        <Link to="/enforcement">Back to Plate Lookup</Link>
        <Link to="/spacemonkey/dashboard">Owner Dashboard</Link>
      </div>
    </div>
  );
};

export default EnforcementScanPage;
